/**
 * DrawerAssetPool - 3-col Grid der Produkt-Assets im ProductActionDrawer
 * Plan 012: Merge Products + Channel Readiness
 *
 * Zeigt globale Assets mit Channel-Tagging-Pills (3 States: neutral/success/error).
 */

import { ImageOff } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/v2/ui'
import type { ProductAsset } from '@/types/products'
import type { ReadinessScore } from '@/lib/api/channel-readiness'

interface DrawerAssetPoolProps {
  assets: ProductAsset[]
  channelScores: ReadinessScore[]
  channelNames: Record<string, string>
}

type PillVariant = 'secondary' | 'success' | 'error'

function channelPillVariant(channelId: string | null, scores: ReadinessScore[]): PillVariant {
  if (!channelId) return 'secondary'
  const score = scores.find(s => s.channel_id === channelId)
  if (!score) return 'secondary'
  return score.issue_count > 0 ? 'error' : 'success'
}

export function DrawerAssetPool({ assets, channelScores, channelNames }: DrawerAssetPoolProps) {
  const { t } = useTranslation()

  if (assets.length === 0) {
    return (
      <p className="drawer-asset-pool text-sm text-neutral-500 px-4 py-3">
        {t('products.drawer_no_assets')}
      </p>
    )
  }

  return (
    <div className="drawer-asset-pool px-4 py-3 space-y-2">
      <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
        {t('products.drawer_asset_pool')}
      </h3>
      <div className="grid grid-cols-3 gap-2">
        {assets.slice(0, 9).map((asset, i) => {
          const variant = channelPillVariant(asset.channel_id ?? null, channelScores)
          const channelLabel = asset.channel_id
            ? (channelNames[asset.channel_id] ?? asset.channel_name ?? asset.channel_id)
            : t('products.allChannels')

          return (
            <div key={asset.link_id} className="space-y-1">
              <div className="relative aspect-square rounded overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                {asset.s3_url
                  ? <img src={asset.s3_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center">
                      <ImageOff className="w-5 h-5 text-neutral-400" />
                    </div>
                }
                <span className="absolute top-1 left-1 bg-black/60 text-white text-xs font-medium rounded px-1 leading-5">
                  {i + 1}
                </span>
              </div>
              <Badge variant={variant} className="text-xs max-w-full truncate block">
                {channelLabel}
              </Badge>
            </div>
          )
        })}
      </div>
    </div>
  )
}
